import {useState, useCallback, useEffect, useMemo} from 'react';

import type {AddButtonPropsSingular, EmptyStateProps} from 'shells/card/card-widget/card-widget';
import {LeadChevronRegularIcon} from 'shells/icon';
import {LeadRow} from 'shells/row/lead-row/lead-row';
import {PageTabs} from 'shells/tabs/page-tabs/page-tabs';

import type {
    PeepRelatedLeadFragment as Lead,
    PeepRelatedLeadStatsFragment,
    WidgetFragment,
} from 'nutshell-graphql-types';

import {CollapsibleCardWidget} from '../../../core/card/collapsible-card-widget';
import {useUpdateWidgetCollapseState} from '../../../core/card/graphql/hooks';
import {WidgetSeeAllFooter} from '../../../core-entity/components/widget-see-all-footer';
import {useHasSmallSidebar} from '../../../core-entity/hooks/use-has-small-sidebar';

import {appHistory} from '../../../../history';

import {
    getCountForSelectedTab,
    getFirstTabWithCount,
    getLeadsList,
    getStatusCounts,
    getTabOptions,
} from './helpers';
import {LeadStats} from './lead-stats';
import type {WidgetLeadStatus} from './leads-widget';

import styles from './leads-widget.module.css';

const INITIAL_VISIBLE_LEADS = 3;

export interface Props {
    widget: WidgetFragment;
    leads: Lead[] | undefined;
    stats: PeepRelatedLeadStatsFragment | null | undefined;
    isLoading: boolean;
    addButtonProps?: AddButtonPropsSingular;
    getSeeAllLink?: (tab: WidgetLeadStatus) => string;
}

export function LeadsWidgetContent(props: Props) {
    const [selectedTab, setSelectedTab] = useState<WidgetLeadStatus>('Open');
    const [error, setError] = useState<string | null | undefined>(null);
    const [isExpanded, setIsExpanded] = useState(false);

    const {updateWidgetCollapseState} = useUpdateWidgetCollapseState();
    const hasSmallSidebar = useHasSmallSidebar();

    const allLeadsList = useMemo(() => {
        return getLeadsList(props.leads, selectedTab);
    }, [props.leads, selectedTab]);

    const hasMore = allLeadsList.length > INITIAL_VISIBLE_LEADS;

    const leadsList = useMemo(() => {
        if (isExpanded) return allLeadsList;

        return allLeadsList.slice(0, INITIAL_VISIBLE_LEADS);
    }, [allLeadsList, isExpanded]);

    const statusCounts = useMemo(() => {
        return getStatusCounts(props.stats);
    }, [props.stats]);

    useEffect(() => {
        if (props.widget.isCollapsedForUser) {
            setIsExpanded(false);
        }
    }, [props.widget.isCollapsedForUser]);

    useEffect(() => {
        if (getCountForSelectedTab(statusCounts, selectedTab) === 0) {
            const firstTab = getFirstTabWithCount(statusCounts);
            if (firstTab) setSelectedTab(firstTab);
        }
    }, [statusCounts, selectedTab]);

    const handleCollapsedTabClick = useCallback(
        (tab: WidgetLeadStatus) => {
            setSelectedTab(tab);
            setIsExpanded(false);
            updateWidgetCollapseState({
                widget: props.widget.id,
                isCollapsed: false,
            });
        },
        [updateWidgetCollapseState, props.widget.id]
    );

    const handleTabChange = useCallback((tab: WidgetLeadStatus) => {
        setSelectedTab(tab);
        setIsExpanded(false);
    }, []);

    const tabProps = useMemo(() => {
        return getTabOptions(selectedTab, handleTabChange, statusCounts, hasSmallSidebar);
    }, [selectedTab, handleTabChange, statusCounts, hasSmallSidebar]);

    const collapsedTabs = useMemo(
        () =>
            tabProps.options.map((option) => ({
                id: option.id,
                label: option.label,
                count: option.count,
                isActive: false,
                badgeColor: option.badgeColor || undefined,
                // @ts-expect-error
                onClick: () => handleCollapsedTabClick(option.id),
            })),
        [tabProps.options, handleCollapsedTabClick]
    );

    const emptyStateProps: EmptyStateProps = useMemo(() => {
        return {
            isEmpty:
                statusCounts.openCount === 0 &&
                statusCounts.wonCount === 0 &&
                statusCounts.lostCount === 0 &&
                statusCounts.cancelledCount === 0,
            text: 'No associated leads',
            icon: LeadChevronRegularIcon,
            iconSize: 22,
        };
    }, [statusCounts]);

    const headerContent = useMemo(() => {
        if (!props.stats) {
            return null;
        }

        return <LeadStats stats={props.stats} hasSmallSidebar={hasSmallSidebar} />;
    }, [props.stats, hasSmallSidebar]);

    return (
        <CollapsibleCardWidget
            widget={props.widget}
            isLoading={props.isLoading}
            addButtonProps={props.addButtonProps}
            emptyStateProps={emptyStateProps}
            errorProps={{
                error: error,
                setError: setError,
            }}
            hasBorder={true}
            tabProps={{
                tabs: tabProps,
                headerContent: headerContent,
            }}
            collapsedContent={
                collapsedTabs.length > 0 ? (
                    <div className='px-12'>
                        <PageTabs tabs={collapsedTabs} lessPadding={true} />
                    </div>
                ) : undefined
            }
        >
            <div className={isExpanded ? styles['scroll-container'] : undefined}>
                {leadsList.map((lead) => (
                    <LeadRow
                        key={lead.id}
                        title={lead.name}
                        onClick={() => {
                            appHistory.push(`/lead/${lead.number}`);
                        }}
                        status={lead.status}
                        assignee={lead.assignee}
                        relatedAccount={lead.accounts?.primaryEdge?.node}
                        relatedContact={lead.contacts?.primaryEdge?.node}
                        value={lead.value?.formatted}
                        avatarUrl={lead.avatarUrl}
                    />
                ))}
            </div>
            <WidgetSeeAllFooter
                seeAllProps={{
                    isExpanded,
                    hasMore,
                    term: `${selectedTab.toLowerCase()} leads`,
                    onClick: () => setIsExpanded(true),
                }}
                listLink={props.getSeeAllLink?.(selectedTab)}
                listLinkItemCount={getCountForSelectedTab(statusCounts, selectedTab)}
            />
        </CollapsibleCardWidget>
    );
}
